import React, { useState } from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import Icon from './AppIcon';
import config from '../config';

const AppHeader = ({ user, currentProject, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Detect if we're in a project context
  const isInProjectContext = Boolean(projectId) || location.pathname.includes('/projects/');
  const currentProjectId = projectId || location.pathname.match(/\/projects\/([^/]+)/)?.[1];

  const isActiveTab = (pattern) => location.pathname.includes(pattern);

  const projectTabs = [
    { path: `/projects/${currentProjectId}/editor`, label: 'Редактор', icon: 'Layout', pattern: '/editor' },
    { path: `/projects/${currentProjectId}/preview`, label: 'Просмотр', icon: 'Eye', pattern: '/preview' },
    { path: `/projects/${currentProjectId}/settings`, label: 'Настройки', icon: 'Settings', pattern: '/settings' }
  ];

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    navigate('/login');
  };

  const projectName = currentProject?.name || 'Сайт';

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-2 right-4 z-[60] p-2 bg-card rounded-lg border border-border shadow-sm"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Меню"
      >
        <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={20} />
      </button>

      {/* Main Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border">
        {/* Top bar - always visible */}
        <div className="h-14 px-4 lg:px-6 flex items-center justify-between max-w-screen-2xl mx-auto">
          {/* Left side: Logo + Breadcrumb */}
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Layers" size={18} color="#FFFFFF" />
              </div>
              <span className="font-semibold text-foreground hidden sm:block">{config.appName}</span>
            </Link>

            {/* Breadcrumb - only in project context */}
            {isInProjectContext && (
              <div className="flex items-center gap-2 text-sm">
                <Icon name="ChevronRight" size={16} className="text-text-secondary" />
                <Link
                  to="/dashboard"
                  className="text-text-secondary hover:text-foreground transition-colors hidden sm:block"
                >
                  Сайты
                </Link>
                <Icon name="ChevronRight" size={16} className="text-text-secondary hidden sm:block" />
                <span className="font-medium text-foreground truncate max-w-[150px] sm:max-w-[200px]">
                  {projectName}
                </span>
              </div>
            )}
          </div>

          {/* Right side: User menu */}
          {user && (
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted transition-colors"
              >
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} className="text-primary" />
                </div>
                <span className="text-sm text-text-secondary max-w-[100px] truncate hidden sm:block">
                  {user?.name || user?.email}
                </span>
                <Icon name="ChevronDown" size={16} className="text-text-secondary hidden sm:block" />
              </button>

              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-56 bg-card rounded-xl border border-border shadow-lg z-50 py-2 overflow-hidden">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="font-medium text-foreground truncate">{user?.name || 'Пользователь'}</p>
                      <p className="text-sm text-text-secondary truncate">{user?.email}</p>
                    </div>
                    <div className="py-2">
                      <button
                        onClick={() => {
                          navigate('/account');
                          setUserMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                      >
                        <Icon name="Settings" size={18} />
                        Настройки аккаунта
                      </button>
                      <button
                        onClick={() => {
                          navigate('/dashboard');
                          setUserMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                      >
                        <Icon name="LayoutDashboard" size={18} />
                        Мои сайты
                      </button>
                    </div>
                    <div className="border-t border-border pt-2">
                      <button
                        onClick={() => {
                          handleLogout();
                          setUserMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-error hover:bg-muted transition-colors"
                      >
                        <Icon name="LogOut" size={18} />
                        Выйти
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Project tabs - only in project context */}
        {isInProjectContext && (
          <div className="hidden lg:block border-t border-border bg-muted/30">
            <div className="px-6 max-w-screen-2xl mx-auto">
              <nav className="flex gap-1">
                {projectTabs.map((tab) => (
                  <Link
                    key={tab.path}
                    to={tab.path}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      isActiveTab(tab.pattern)
                        ? 'border-primary text-primary'
                        : 'border-transparent text-text-secondary hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon name={tab.icon} size={18} />
                    {tab.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        )}
      </header>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-background">
          <div className="flex flex-col h-full">
            {/* Mobile header */}
            <div className="h-14 px-4 flex items-center justify-between border-b border-border">
              <Link to="/dashboard" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Layers" size={18} color="#FFFFFF" />
                </div>
                <span className="font-semibold">{config.appName}</span>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            {/* Mobile navigation */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Back to projects */}
              <Link
                to="/dashboard"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors mb-2"
              >
                <Icon name="LayoutDashboard" size={20} />
                <span className="font-medium">Мои сайты</span>
              </Link>

              {/* Project tabs - only in project context */}
              {isInProjectContext && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="px-4 py-2 text-sm font-medium text-text-secondary">
                    {projectName}
                  </p>
                  {projectTabs.map((tab) => (
                    <Link
                      key={tab.path}
                      to={tab.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActiveTab(tab.pattern)
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-muted text-foreground'
                      }`}
                    >
                      <Icon name={tab.icon} size={20} />
                      <span className="font-medium">{tab.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile user section */}
            {user && (
              <div className="p-4 border-t border-border">
                <div className="flex items-center gap-3 px-4 py-3 mb-4 bg-muted rounded-lg">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{user?.name || 'Пользователь'}</p>
                    <p className="text-sm text-text-secondary truncate">{user?.email}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Link
                    to="/account"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors"
                  >
                    <Icon name="Settings" size={20} />
                    <span>Настройки аккаунта</span>
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-error hover:bg-muted transition-colors"
                  >
                    <Icon name="LogOut" size={20} />
                    <span>Выйти</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AppHeader;
